import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'

const DIMENSIONS = [
  {
    id: 'capability',
    title: 'Capability fit',
    description: 'Do we have the right people and partnerships to do this well?',
    questions: [
      {
        id: 'internal_skills',
        text: 'Do we have relevant internal digital skills?',
        description: 'If your team doesn’t understand digital work, it’s hard to make good decisions or spot problems early. The more relevant experience you have in-house, the more confident and informed your digital work will be.',
        options: [
          'We have no internal digital capability. Even basic concepts feel unfamiliar.',
          'Some individuals are digitally literate, but lack the relevant skills for this type of work.',
          'We have moderate internal skills and experience in digital projects.',
          'Our internal team is confident and can manage suppliers or deliver simple solutions themselves.',
          'We have strong in-house expertise across roles and project types. We could mentor others.',
        ]
      },
      {
        id: 'external_partners',
        text: 'Do we have trusted external partners or advisors?',
        description: 'Trusted suppliers and advisors can save time, avoid mistakes, and offer strategic support. Without them, you risk reinventing the wheel or being led by the wrong people.',
        options: [
          'We have no trusted digital partners and no support network.',
          'We’ve worked with external suppliers before, but not consistently or effectively.',
          'We have a few partners we return to, but not always a great strategic fit.',
          'We have a network of go-to partners who understand our needs.',
          'We have strong, strategic relationships with trusted partners across a range of specialisms and disciplines who challenge and support us.',
        ]
      }
    ]
  },
  {
    id: 'culture',
    title: 'Cultural readiness',
    questions: [
      {
        id: 'readiness',
        text: 'Will our people adopt and support this, or will it create friction?',
        description: 'Even good digital ideas can fail if your people aren’t on board. If staff feel excluded or overloaded, they’ll resist or disengage. If they feel involved and see the value, they’re more likely to support it.',
        options: [
          'Digital is seen as a threat or burden. Resistance to change is high.',
          'There’s interest, but fatigue or past failures have made people sceptical.',
          'Some staff are positive, but others are unsure or disengaged.',
          'Digital change is generally welcomed. Staff show curiosity and openness.',
          'There’s strong shared ownership. Digital is seen as a tool for delivering our mission.',
        ]
      }
    ]
  },
  {
    id: 'strategy',
    title: 'Strategic fit',
    description: 'Does this support our mission and solve a real problem?',
    questions: [
      {
        id: 'alignment',
        text: 'Is this decision aligned with our long-term strategy?',
        description: 'Projects that don’t link clearly to your goals often lose momentum. A strong strategic fit helps with focus, internal support, and long-term value.',
        options: [
          'No clear link to strategy. This feels reactive or driven by funding.',
          'There’s a vague connection to strategy, but it’s not convincing.',
          'It’s broadly aligned, but success metrics or strategic impact are unclear.',
          'This clearly supports our existing priorities, future ambitions and direction.',
          'This flows directly from a well-articulated strategy and we can reasonably expect it to create long-term value.',
        ]
      },
      {
        id: 'problem_validity',
        text: 'Are we solving a real and relevant problem?',
        description: 'If you’re unclear about what you’re solving (or if the problem isn’t real) you can waste time, money, and goodwill. You need to be sure the problem matters.',
        options: [
          'The problem is vague, assumed, or driven by external pressure or priorities.',
          'We think there’s a problem, but it hasn’t been clearly defined or tested.',
          'We’ve defined the problem internally, but haven’t validated it.',
          'The problem is well understood and connected to practical needs.',
          'It’s clearly defined, validated with evidence, and agreed across the team.',
        ]
      },
      {
        id: 'success_definition',
        text: 'Do we have a clear, shared definition of success?',
        description: 'Without a shared idea of what success looks like, it’s hard to steer a project or know whether it worked. Agreeing upfront helps avoid confusion and keeps everyone aligned.',
        options: [
          'There’s no shared sense of what success means.',
          'Different people have different ideas of success.',
          'We’ve talked about success informally, but it’s not written down.',
          'There’s a clear, shared understanding of success and how we’ll track it.',
          'Success is well-defined, documented, and linked to our strategic goals and user needs. We’re set up to measure it effectively.',
        ]
      }

    ]
  },
  {
    id: 'purpose',
    title: 'User insight & rationale',
    description: 'Are we building the right thing, in the right way, for the right people?',
    questions: [
      {
        id: 'user_needs',
        text: 'Do we understand the user needs this solution addresses?',
        description: 'Digital work should meet real needs. Without user insight, you’re guessing—and guesses often miss the mark.',
        options: [
          'We have no insight into user needs or behaviours.',
          'We’ve made assumptions based on internal views.',
          'We have some user knowledge, but it’s not specific to this challenge.',
          'We’ve explored real user needs that link to this solution.',
          'We’ve done credible, relevant user research and we are building from real insights that are specific to this project.',
        ]
      },
      {
        id: 'solution_rationale',
        text: 'Are we confident this is the right approach to solving the problem?',
        description: 'There are always options. Choosing one without weighing up others or testing assumptions often leads to regret later.',
        options: [
          'We chose this based on what others are doing or a strong opinion.',
          'We’re going with the default or most familiar option.',
          'We’ve done a bit of comparison, but we didn’t test our assumptions.',
          'We’ve considered options and have a reasoned case for this route.',
          'We’ve tested or compared approaches and are confident this is the best fit and can explain why.',
        ]
      }
    ]
  },
  {
    id: 'ownership',
    title: 'Ownership & longevity',
    description: 'Can we support and improve this after it’s launched?',
    questions: [
      {
        id: 'future_support',
        text: 'Have we planned for long-term support and improvement?',
        description: ' Many digital projects fail after launch because there’s no plan to maintain or develop them. Thinking ahead avoids waste.',
        options: [
          'We haven’t thought about what happens after launch.',
          'We hope to figure it out later, but nothing is planned at the moment.',
          'There’s a vague plan for future support, but not specific or resourced.',
          'We’ve planned for handover, support, and updates.',
          'There’s a clear roadmap, budget, and capacity for long-term evolution.',
        ]
      },
      {
        id: 'knowledge_retention',
        text: 'Will knowledge and responsibility be retained internally?',
        description: ' If all the know-how sits with one person or outside the organisation, you’re at risk. Good handover and shared understanding make projects more resilient.',
        options: [
          'All knowledge sits with external suppliers or a single staff member.',
          'Some team members are involved, but it’s fragmented and informal.',
          'We’ve tried to document things but haven’t tested things to confirm that knowledge is shared.',
          'We’ve documented and trained internally to build resilience.',
          'We’ve built robust internal ownership with strong documentation and team capacity, we’ve tested this and know it works.',
        ]
      }
    ]
  },
  {
    id: 'risk',
    title: 'Risk & dependency',
    description: 'Do we know what might go wrong and can we recover if it does?',
    questions: [
      {
        id: 'risk_awareness',
        text: 'Have we identified major risks and dependencies?',
        description: ' All projects have risks. Naming them early lets you prepare instead of panic.',
        options: [
          'We haven’t discussed risks or dependencies at all.',
          'We’ve mentioned risks, but we haven’t explored or documented them.',
          'We’ve identified some risks, but our plans are limited.',
          'Risks are mapped and understood, and we have practical mitigation plans.',
          'We proactively manage risks, monitor dependencies, and adapt accordingly.',
        ]
      },
      {
        id: 'exit_strategy',
        text: 'Can we exit or pivot if the solution doesn’t work?',
        description: ' If something isn’t working, can you change course? Having options protects you from being stuck with bad choices.',
        options: [
          'No exit strategy. We’d be stuck if things went wrong.',
          'Some fallback ideas exist, but they’re not formalised.',
          'There’s a basic potential exit route or alternative, but it’s high-effort or unclear.',
          'We’ve planned for pivoting or changing suppliers if needed.',
          'We’ve designed modularly or used open standards to keep options open.',
        ]
      }
    ]
  },
  {
    id: 'resourcing',
    title: 'Resourcing & time',
    description: 'Do we have the money, people, and time to do this properly?',
    questions: [
      {
        id: 'budget_fit',
        text: 'Is the budget realistic and aligned to needs?',
        description: ' Too little budget means cutting corners or failing to deliver. A realistic budget makes success more likely.',
        options: [
          'No budget or totally insufficient funding.',
          'There’s a very small or token budget.',
          'Budget covers core delivery but little else.',
          'Budget matches the scope and includes delivery and support.',
          'Budget allows for delivery, learning, support, and future iteration.',
        ]
      },
      {
        id: 'staff_capacity',
        text: 'Do staff have time and headspace to deliver this?',
        description: ' Even with funding, if your people don’t have time, the project will suffer. Protecting time shows you’re serious.',
        options: [
          'No capacity. Everyone is already at full stretch.',
          'Good intentions, but people can’t make time in practice.',
          'Some time is protected, but not across the whole team.',
          'Key people have been freed up to focus.',
          'The team has clear, sustained time to work on and embed this.',
        ]
      },
      {
        id: 'timeline_realism',
        text: 'Is the delivery timeline realistic?',
        description: ' Unrealistic deadlines add stress and reduce quality. Time to plan, test, and reflect makes better outcomes.',
        options: [
          'The deadline is aspirational and unachievable.',
          'It’s extremely tight and would mean cutting corners.',
          'It’s possible, but doesn’t leave much room for testing or issues.',
          'Timelines are realistic and account for internal rhythms.',
          'We’ve allowed time for planning, testing, delivery, and reflection.',
        ]
      }
    ]
  },
  {
    id: 'decision',
    title: 'Decision-making quality',
    description: 'Did we make a considered, inclusive decision or just react?',
    questions: [
      {
        id: 'inclusive_process',
        text: 'Were the right people involved in making this decision?',
        description: ' Good decisions involve the right people. If key voices are missed, problems show up later.',
        options: [
          'Made by one or two senior people without consultation.',
          'A few stakeholders were asked, but key voices were missing.',
          'Consulted some teams, but process lacked structure.',
          'Relevant people were involved with transparency.',
          'Inclusive, well-facilitated process with broad engagement and buy-in.',
        ]
      },
      {
        id: 'alternatives_considered',
        text: 'Did we consider meaningful alternatives?',
        description: ' Rushing to a single solution without comparing options can lead to poor fit or missed opportunities.',
        options: [
          'We jumped to a decision without considering options.',
          'Looked at one or two ideas, but not in depth.',
          'We considered a few options briefly.',
          'We compared alternatives and discussed trade-offs.',
          'We rigorously compared different approaches or suppliers before choosing.',
        ]
      }
    ]
  },
  {
    id: 'experience',
    title: 'Experience fit',
    questions: [
      {
        id: 'past_experience',
        text: 'Have we done anything like this before and what did we learn?',
        description: ' Past projects (even failures) are a valuable source of learning. Applying those lessons helps avoid repeat mistakes.',
        options: [
          'No similar experience or memory of past attempts.',
          'We’ve done something once but didn’t reflect on it.',
          'We’ve learned some lessons, but they aren’t well applied.',
          'Our experience is relevant and shaping our approach.',
          'We have rich, recent experience and are applying it deliberately.',
        ]
      }
    ]
  },
  {
    id: 'learning',
    title: 'Learning & adaptability',
    description: "Can we learn and adjust as we go, or are we stuck on one track?",
    questions: [
      {
        id: 'reflection_routines',
        text: 'Do we have routines for reflection and learning?',
        description: ' If you don’t stop to reflect, you won’t improve. Regular reviews make teams stronger and projects smarter.',
        options: [
          'We rarely reflect or discuss how things went.',
          'Some learning happens, but it’s informal and not shared.',
          'We hold occasional reviews, but follow-up is patchy.',
          'We document and share lessons across teams.',
          'Learning is embedded. We act on insights and improve consistently, it’s a normal part of the way we work.',
        ]
      },
      {
        id: 'adaptability',
        text: 'Can we adapt based on feedback and change course?',
        description: ' Feedback is only useful if you act on it. Flexibility allows you to respond to change and improve outcomes.',
        options: [
          'We keep going even if things aren’t working.',
          'We resist change until problems are obvious.',
          'We sometimes adapt, but it’s inconsistent.',
          'We actively make informed changes based on new insights or changing conditions.',
          'We’re built for adaptability. Iteration is expected and resourced.',
        ]
      }
    ]
  }
]

function FormPage() {
  const navigate = useNavigate()
  const [scores, setScores] = useState({})
  const [showIntro, setShowIntro] = useState(false)
  // URL query param handle
  const [searchParams, setSearchParams] = useSearchParams()
  // NEW: section-by-section navigation state
  const [currentSection, setCurrentSection] = useState(0)   // which section is visible
  const [sectionError, setSectionError] = useState('')      // validation message
  const sectionCount = DIMENSIONS.length                    // total sections
  const firstUnansweredRef = useRef(null)                   // for scrolling to missing answer
  // On mount: read ?step= from the URL and move to that section (1-based in URL)
  useEffect(() => {
    const raw = searchParams.get('step')
    const n = parseInt(raw || '1', 10)
    if (Number.isFinite(n)) {
      const idx = Math.min(Math.max(n - 1, 0), sectionCount - 1)
      if (idx !== currentSection) setCurrentSection(idx)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // run once on first render

  // When currentSection changes, update ?step= in the URL (replace so Back behaves nicely)
  useEffect(() => {
    const step = String(currentSection + 1)
    if (searchParams.get('step') !== step) {
      const next = new URLSearchParams(searchParams)
      next.set('step', step)
      setSearchParams(next, { replace: true })
    }
  }, [currentSection, searchParams, setSearchParams])


  // Optional: react if the user manually edits ?step= while the page is open
  useEffect(() => {
    const raw = searchParams.get('step')
    const n = parseInt(raw || '1', 10)
    if (!Number.isFinite(n)) return
    const idx = Math.min(Math.max(n - 1, 0), sectionCount - 1)
    if (idx !== currentSection) setCurrentSection(idx)
  }, [searchParams, sectionCount]) // runs whenever the URL query changes
  

  const submitToBackend = async () => {
  const storedEmail = localStorage.getItem('userEmail') || null

  try {
    const response = await fetch('/.netlify/functions/submitResults', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: storedEmail,
        consent: true,
        scores,
      }),
    })

    const result = await response.json()
    if (result.success) {
      // ✅ Clear the stored email after successful submission
      localStorage.removeItem('userEmail')
    } else {
      console.error('Backend error:', result)
    }
  } catch (error) {
    console.error('Submission failed:', error)
  }
}

  // --- Section validation & navigation helpers ---
  // Is every question in the given section answered?
  const isSectionComplete = (dim) =>
    dim.questions.every((q) => typeof scores[q.id] === 'number')

  // Scroll to the first unanswered question in the section
  const focusFirstUnanswered = (dim) => {
    const firstUnanswered = dim.questions.find((q) => typeof scores[q.id] !== 'number')
    if (!firstUnanswered) return
    const el = document.getElementById(`${dim.id}-${firstUnanswered.id}-group`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Go forward one section (only if complete)
  // Go forward one section (only if complete) — scrolls to next section title
  const handleNext = () => {
    const dim = DIMENSIONS[currentSection]
    if (!isSectionComplete(dim)) {
      setSectionError('Please answer all questions in this section to continue.')
      focusFirstUnanswered(dim)
      return
    }
    setSectionError('')

    const nextIndex = Math.min(currentSection + 1, sectionCount - 1)
    setCurrentSection(nextIndex)

    // After the DOM updates, smoothly scroll the next section's legend into view
    setTimeout(() => {
      const nextDim = DIMENSIONS[nextIndex]
      const legend = document.getElementById(`${nextDim.id}-legend`)
      if (legend) legend.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }

  // Go back one section — scrolls to previous section title
  const handleBack = () => {
    setSectionError('')

    const prevIndex = Math.max(currentSection - 1, 0)
    setCurrentSection(prevIndex)

    // After the DOM updates, smoothly scroll the previous section's legend into view
    setTimeout(() => {
      const prevDim = DIMENSIONS[prevIndex]
      const legend = document.getElementById(`${prevDim.id}-legend`)
      if (legend) legend.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }



  const handleSubmit = async (e) => {
  e.preventDefault()
  // Block final submit if current section incomplete
  const dimCheck = DIMENSIONS[currentSection]
  if (!isSectionComplete(dimCheck)) {
    setSectionError('Please answer all questions in this section to continue.')
    focusFirstUnanswered(dimCheck)
    return
  }
  await submitToBackend()
  const DIMENSION_IDS = {
  capability: ['internal_skills', 'external_partners'],
  culture: ['readiness'],
  strategy: ['alignment', 'problem_validity', 'success_definition'],
  purpose: ['user_needs', 'solution_rationale'],
  ownership: ['future_support', 'knowledge_retention'],
  risk: ['risk_awareness', 'exit_strategy'],
  resourcing: ['budget_fit', 'staff_capacity', 'timeline_realism'],
  decision: ['inclusive_process', 'alternatives_considered'],
  experience: ['past_experience'],
  learning: ['reflection_routines', 'adaptability'],
}

const averagedScores = Object.entries(DIMENSION_IDS).reduce((acc, [dimension, questions]) => {
  const values = questions
    .map((q) => scores[q])
    .filter((val) => typeof val === 'number')
  const avg = values.length
    ? Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)
    : null
  acc[dimension] = avg
  return acc
}, {})

navigate('/results', { state: { scores: averagedScores, subScores: scores, } })

}

const totalQuestions = DIMENSIONS.reduce((sum, dim) => sum + dim.questions.length, 0)
const answeredCount = Object.keys(scores).filter((key) => typeof scores[key] === 'number').length
const progress = Math.round((answeredCount / totalQuestions) * 100)

  // show only the currently active section
  const dim = DIMENSIONS[currentSection]

  return (
    <div className="page-container">
    <form className="form-content" onSubmit={handleSubmit}>
      <div className="sticky-progress-wrapper">
      <p className="progress-label">
        {answeredCount} of {totalQuestions} questions answered ({progress}%)
      </p>
      <div className="progress-bar-container" aria-label={`Form progress: ${progress}%`}>
        <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
      </div>
      </div>

      <h1 className="tool-title">Decision Fit Canvas</h1>
      <p className="tool-subtitle">A free tool from Ash Mann,<br />find more at <a href="https://ashmann.co/tools" target="_blank" rel="noopener noreferrer">ashmann.co/tools <svg
      className="tool-subtitle-icon"
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg></a></p>

  {/* Step indicator (goes immediately above the fieldset) */}
  <div className="section-header mb-4">
    <div className="step-indicator text-sm text-gray-600">
      Section {currentSection + 1} of {sectionCount}
    </div>
  </div>

  {/* Single, active section only */}
  <div className="option-group">
    <fieldset
      className="border border-[#ffeaf2] p-4 mb-6 rounded"
      aria-labelledby={`${dim.id}-legend`}
    >
      <legend id={`${dim.id}-legend`} className="gh-section-title">
        {dim.title}
      </legend>

      {dim.description && (
        <p className="gh-section-description mb-3 text-sm text-gray-600">
          {dim.description}
        </p>
      )}

      {dim.questions.map((q) => (
        <div key={q.id} id={`${dim.id}-${q.id}-group`} className="mb-4">
          <h3 id={`${dim.id}-${q.id}-desc`} className="mb-2 text-gray-700">
            {q.text}
          </h3>

          {q.description && (
            <p className="sub-q-description mb-3 text-sm text-gray-600">
              {q.description}
            </p>
          )}

          <div role="radiogroup" aria-labelledby={`${dim.id}-${q.id}-desc`}>
            {q.options.map((opt, i) => {
              const inputId = `${dim.id}-${q.id}-${i + 1}`
              return (
                <div key={inputId} className="option-item">
                  <label htmlFor={inputId} className="option-label">
                    <input
                      type="radio"
                      id={inputId}
                      name={q.id}
                      value={i + 1}
                      checked={scores[q.id] === i + 1}
                      onChange={() =>
                        setScores((prev) => ({ ...prev, [q.id]: i + 1 }))
                      }
                      required
                    />
                    <span>{opt}</span>
                  </label>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </fieldset>
  </div>




      {/* Section-level error */}
{sectionError && (
   <div className="form-error text-red-600 text-sm mb-4" role="alert" aria-live="polite">
     {sectionError}
   </div>
 )}

      {/* Back/Next or Submit-on-final */}
      <div className="section-nav flex items-center gap-3">
        {currentSection > 0 && (
          <button type="button" className="btn btn-secondary" onClick={handleBack}>
            ◀ Back
          </button>
        )}

        {currentSection < sectionCount - 1 ? (
          <button type="button" className="btn btn-primary" onClick={handleNext}>
            Save & continue ▶
          </button>
        ) : (
          <button type="submit" className="btn btn-primary">
            View your results and next steps
          </button>
        )}
      </div>
    </form>
    </div>
  )
}

export default FormPage